import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, Trophy, Check, CheckCircle, Loader2, X, UserPlus } from 'lucide-react';
import { getLocalizedField, pickLocalizedText } from '../../utils/localizedContent';

const TCG_COLORS = { pokemon: '#D4A84B', magic: '#0D5E4A', yugioh: '#C45B4A', board_game: '#2D8B70', all: '#C9A962' };

export default function EventHeader({ 
  event, 
  participants,
  isRegistered, 
  isCheckedIn, 
  onCheckIn,
  onRegister,
  actionLoading,
  lang,
  tournamentTypeLabel
}) {
  const isFull = participants.total_registered >= event.max_participants;
  const canRegister = event.allow_registration !== false && event.status === 'upcoming';
  const isTournamentDeployed = event?.event_type === 'tournament' && !!event?.participant_organization;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span 
              className="px-3 py-1 rounded-full text-xs font-bold" 
              style={{ 
                backgroundColor: `${TCG_COLORS[event.tcg_type] || TCG_COLORS.all}20`, 
                color: TCG_COLORS[event.tcg_type] || TCG_COLORS.all 
              }}
            >
              {event.tcg_type?.toUpperCase()}
            </span>
            <StatusBadge status={event.status} lang={lang} />
            {tournamentTypeLabel && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                {tournamentTypeLabel}
              </span>
            )}
          </div>
          <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>
            {getLocalizedField(event, 'title', lang, event.title)}
          </h1>
          <p className="text-muted-foreground mt-2">
            {getLocalizedField(event, 'description', lang, event.description)}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Register / Unregister button */}
          {canRegister && !isTournamentDeployed && (
            <button
              onClick={onRegister}
              disabled={actionLoading === 'register' || (isFull && !isRegistered)}
              className={`px-6 py-2.5 text-sm font-bold rounded-xl flex items-center gap-2 transition-all disabled:opacity-50 ${
                isRegistered
                  ? 'bg-[#C45B4A]/20 text-[#F87171] hover:bg-[#C45B4A]/30 border border-[#C45B4A]/20'
                  : isFull
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'btn-primary'
              }`}
              data-testid="register-btn"
            >
              {actionLoading === 'register'
                ? <Loader2 size={16} className="animate-spin" />
                : isRegistered
                ? <><X size={16} />{pickLocalizedText(lang, 'Cancelar inscripcion', 'Unregister')}</>
                : isFull
                ? pickLocalizedText(lang, 'Completo', 'Full')
                : <><UserPlus size={16} />{pickLocalizedText(lang, 'Inscribirse', 'Register')}</>
              }
            </button>
          )}
          {/* Check-in button */}
          {isRegistered && !isCheckedIn && event.status === 'upcoming' && (
            <button 
              onClick={onCheckIn} 
              disabled={actionLoading === 'checkin'} 
              className="btn-secondary px-6 py-2.5 text-sm flex items-center gap-2" 
              data-testid="checkin-btn"
            >
              {actionLoading === 'checkin' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {pickLocalizedText(lang, 'Hacer Check-in', 'Check In')}
            </button>
          )}
          {isCheckedIn && (
            <span className="px-4 py-2 bg-[#10B981]/20 text-[#34D399] rounded-lg text-sm font-semibold flex items-center gap-2">
              <CheckCircle size={16} /> {pickLocalizedText(lang, 'Check-in completado', 'Checked In')}
            </span>
          )}
        </div>
      </div>

      {/* Event Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <DetailItem icon={Calendar} label={pickLocalizedText(lang, 'Fecha', 'Date')} value={event.date} />
        <DetailItem icon={Clock} label={pickLocalizedText(lang, 'Hora', 'Time')} value={event.time} />
        <DetailItem icon={Users} label={pickLocalizedText(lang, 'Participantes', 'Participants')} value={`${participants.total_registered} / ${event.max_participants}`} />
        <DetailItem icon={Trophy} label={pickLocalizedText(lang, 'Premio', 'Prize')} value={event.prize || '-'} />
      </div>
    </div>
  );
}

function StatusBadge({ status, lang }) {
  const styles = {
    upcoming: 'bg-[#2D8B70]/20 text-[#34D399]',
    in_progress: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    ready_to_finalize: 'bg-[#3B82F6]/20 text-[#93C5FD]',
    completed: 'bg-[#A1A1AA]/20 text-[#A1A1AA]'
  };
  
  const labels = {
    upcoming: pickLocalizedText(lang, 'Proximo', 'Upcoming'),
    in_progress: pickLocalizedText(lang, 'En curso', 'In Progress'),
    ready_to_finalize: pickLocalizedText(lang, 'Listo para finalizar', 'Ready to Finalize'),
    completed: pickLocalizedText(lang, 'Completado', 'Completed')
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${styles[status] || styles.completed}`}>
      {labels[status] || status}
    </span>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={18} className="text-[#C9A962]" />
      <div>
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="text-foreground font-semibold">{value}</p>
      </div>
    </div>
  );
}
